<script>
	import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
	import { toast } from '@zerodevx/svelte-toast';
	import { projectState } from "@app/store/projectStore.svelte.js";
	import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
	import { speakerState } from "@app/store/speakerStore.svelte.js";
	import { markerState } from "@app/store/markerStore.svelte.js";
	import { issueState } from "@app/store/issueStore.svelte.js";
	import { metadataState } from "@app/store/metadataStore.svelte.js";
	import { styleState } from "@app/store/styleStore.svelte.js";
	import { insertHistoryAction, resetHistory } from "@app/store/historyStore.svelte.js";
	import { authState } from "@app/store/authStore.svelte.js";
	import { fade } from "svelte/transition";
	import { tick } from "svelte";
	import { onDestroy } from "svelte";
	import * as m from "@app/paraglide/messages.js";
	/* Firebase */
	import firebase from "@app/configs/firebase.js";
	import storage from "@app/configs/storage.js";
	import db from "@app/configs/firestore.js";

	/* CC LIB */
	import _Project from "@cclib/classes/project.js";
	import _EventGroup from "@cclib/classes/eventGroup.js";
	import _Event from "@cclib/classes/event.js";
    import { ccprjFunc } from '@app/lib/ccprj.js';

    import Swal from 'sweetalert2'
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";

	let userId = firebase.auth().currentUser.uid;
	let progress = $state(0),
        processing = $state(false),
        commitMessage = $state(""),
		statusMsg = $state(m.team_sync_status_retrieving_latest_commit()),
		selectedEventGroupId = false,
		indexOfSelectedEventGroup = -1,
		unsubscribe;

	function escapeHtml(value) {
		return String(value ?? "")
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;");
	}

	onDestroy(() => {
		if (unsubscribe) {
			unsubscribe();
		}
	});

	async function retrieveSnapshot(commitFileUrl) {
		try {
			console.log("Retrieving Commit File");
			progress = 45;
			statusMsg = m.team_sync_status_retrieving_latest_commit();
			let commitFileRes = await fetch(commitFileUrl);
			let commitFileJson = await commitFileRes.json();
			console.log("COMMIT FILE JSON:", commitFileJson);

			progress = 45;
			statusMsg = m.team_sync_status_loading_commit();

			/* Save place for user */
			if (projectState.selected > -1 && eventGroupState[projectState.selected]) {
				selectedEventGroupId = eventGroupState[projectState.selected].id;
			}

			/* Clear and wait for update... */
			projectState.selected = false;
			await tick();
		
			//console.log("Updating Name and Commit ID");
			projectState.name = commitFileJson.name || "Untitled";
			projectState.commit = commitFileJson.id;

            //Check if there are events groups in the current eventGroupState that are not in the commitFileJson
            //If so, we need to create an array with their event group names.

            let eventGroupsNotInShare = [];
            for (var eventGroup of eventGroupState) {
                let eventGroupInShareIndex = commitFileJson.eventGroups.findIndex((shareEventGroup) => {
                    return shareEventGroup.id === eventGroup.id;
                });

                if (eventGroupInShareIndex === -1) {
                    eventGroupsNotInShare.push(eventGroup.name);
                }
            }

            console.log("Event Groups Not In Share:", eventGroupsNotInShare);

			//console.log("Importing Changes:");
			for (var teamEventGroup of commitFileJson.eventGroups) {
				if (teamEventGroup.state !== "deleted") {
					//console.log("Importing changes from Event Group");
					let teamEvGroup = {...(new _EventGroup({
						...teamEventGroup,
						events: [],
						selected: [],
					}))};

					teamEvGroup.id = teamEventGroup.id;

					for (var eventItem of teamEventGroup.events) {
						if (eventItem.state !== "deleted") {
							//console.log(event);
							let teamEv = {...(new _Event(eventItem))};
							teamEv.id = eventItem.id;
							teamEvGroup.events.push(teamEv);
						}
					}

					//console.log("Commiting event group to event group state");
                    //Check eventGroupState to see if the event group already exists
                    let existingEventGroupIndex = eventGroupState.findIndex((eventGroup) => {
                        return eventGroup.id === teamEvGroup.id;
                    }); 

                    teamEvGroup.scrollPosition = 0;
					eventGroupState[existingEventGroupIndex] = teamEvGroup;
				}
			}

            if (eventGroupsNotInShare.length >0){
                /* Show sweet alert to let user know there are events that are not part of the team project and were not synced with the server. These projects only exist on their system. */
                Swal.fire({
					...SWAL_BASE,
					customClass: SWAL_CLASSES.ok,
					title: m.team_sync_events_not_in_team_project_title(),
					html: `${m.team_sync_events_not_in_team_project_intro()}<br><br> <ul class="list-unstyled">${eventGroupsNotInShare.map((eventGroupName) => {
						return `<li>${escapeHtml(eventGroupName)}</li>`;
                    }).join("")}</ul>`,
                    icon: 'warning',
					confirmButtonText: m.team_sync_events_not_in_team_project_confirm(),
                    allowOutsideClick : false,
                    allowEscapeKey : false,
                });
            }

            
			progress = 100;
			statusMsg = m.team_sync_status_done();

			await tick();

			if (selectedEventGroupId !== false) {
				indexOfSelectedEventGroup = eventGroupState.findIndex((eventGroup) => {
					return eventGroup.id === selectedEventGroupId;
				});

				projectState.selected = indexOfSelectedEventGroup > -1 ? indexOfSelectedEventGroup : 0;
			} else if (eventGroupState.length > 0) {
				projectState.selected = 0;
			}

			resetHistory();
			insertHistoryAction({
				name: "project sync", //action name
				eventGroup: projectState.selected,
				snapshots: [
					{
						store: "eventGroupState",
						value: JSON.stringify(eventGroupState),
					},
				],
			});

			toast.push(m.team_sync_completed_successfully(), {classes: ['toast-success']});
		} catch (err) {
			console.log(err, err.message);
			toast.push(m.team_sync_failed({ message: err.message }), {classes: ['toast-danger']});
		} finally {
			hideModal();
		}
	}

	async function syncProject() {
		toast.push(m.team_sync_starting_info(), {classes: ['toast-info']});

        processing = true;

		try {
			if (!authState.team || !authState.team.id) {
				throw new Error(m.team_sync_missing_team_error());
			}

			let commitFileUrl;
			let teamId = authState.team.id;
			let storageRef = storage.ref();
			let snapshotId = `${Date.now().toString()}_${userId}`; //Snapshot is like a copy of our project
			let fileName = `${snapshotId}.json`;
			/* commits : teams/*teamId/projects/*project.teamId/commits */
			let dbRef = db.collection("teams").doc(teamId).collection("projects").doc(projectState.teamId).collection("snapshots"); //Location in DB so we can track a commit job in case our sync function times out.

			/* snapshots: /teams/*teamId/projects/*project.teamId/snapshots/*date_userId*.json */
			let uploadRef = storageRef.child(`teams/${teamId}/projects/${projectState.teamId}/snapshots/${fileName}`); //Where we will upload our snapshot used by the v8SyncProjectV3 script

			/* Generate a Snapshot of the project using each SVELTE Store */
			let projectJson = ccprjFunc.createCcprj({
                project : JSON.parse(JSON.stringify(projectState)),
                metadata : JSON.parse(JSON.stringify(metadataState)),
                speakers : JSON.parse(JSON.stringify(speakerState)),
                issues : JSON.parse(JSON.stringify(issueState.value)),
                markers : JSON.parse(JSON.stringify(markerState)),
                eventGroups : JSON.parse(JSON.stringify(eventGroupState)),
                style : JSON.parse(JSON.stringify(styleState))
            });

			/* Register Project Sync Commit */
			try {
				progress = 5;
				let registerProjectRes = await firebase.functions().httpsCallable("v8RegisterProjectSync")({
					id: snapshotId,
					projectTeamId: projectState.teamId,
                    message : commitMessage
				});

				if (registerProjectRes && registerProjectRes.data) {
					console.log(JSON.stringify(registerProjectRes));
					throw new Error(registerProjectRes.data);
				}
			} catch (err) {
				/* Another sync may already be in progress. Cancelling for now... */
				console.error("Unable to register snapshot");
				console.log(err, err.message);
				toast.push(m.team_sync_register_job_error({ message: err.message }), {classes: ['toast-danger']});

				hideModal();
				return;
			}

			progress = 15;
			statusMsg = m.team_sync_status_uploading_snapshot();
			await uploadRef.putString(JSON.stringify(projectJson));
			progress = 25;

			statusMsg = m.team_sync_status_syncing_and_generating_commit();
			try {
				commitFileUrl = await firebase.functions().httpsCallable("v8SyncProjectV3")({
					id: snapshotId,
					projectTeamId: projectState.teamId,
				});

				retrieveSnapshot(commitFileUrl.data[0]);
			} catch (err) {
				console.log(err, err.message);
				progress = 35;
				unsubscribe = dbRef.doc(snapshotId).onSnapshot((doc) => {
					console.log(doc.data());
					if (doc.data() === undefined) {
						console.log(err, err.message);
						toast.push(m.team_sync_failed({ message: err.message }), {classes: ['toast-danger']});

						hideModal();
						return;
					}

					let commitData = doc.data();
					if (commitData.status === "in progress") {
						return;
					} else if (commitData.status === "complete") {
						retrieveSnapshot(commitData.signedFileUrl[0]);
					} else if (commitData.status === "failed") {
						toast.push(m.team_sync_commit_status_failed({ message: err.message }), {classes: ['toast-danger']});

						hideModal();
					} else {
						toast.push(m.team_sync_failed({ message: err.message }), {classes: ['toast-danger']});

						hideModal();
					}
				});
			}
		} catch (err) {
			console.log(err, err.message);
			toast.push(m.team_sync_failed({ message: err.message }), {classes: ['toast-danger']});

			hideModal();
		}
	}
</script>

<div 
    transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'teamSync' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="teamSyncTitle"
    aria-modal="true"
    tabindex="-1" 
    id="TeamSync">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
				<h4 class="modal-title" id="teamSyncTitle">{m.team_sync_title()}</h4>
				<button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                {#if processing}
				<p><i class="bi bi-exclamation-diamond-fill" aria-hidden="true"></i> {m.team_sync_leave_window_open_notice()}</p>
                <div class="progress" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-bar bg-primary progress-bar-striped progress-bar-animated" style="width: {progress}%;">{progress}%</div>
                </div>
                <p class="text-center text-muted small" aria-live="polite">{statusMsg}</p>
                {:else}
                <form>
                    <div class="mb-3">
						<label for="commitMessageArea" class="form-label">{m.team_sync_commit_message_label()} <small>({m.team_sync_optional_label()})</small></label>
                        <textarea 
                            bind:value={commitMessage} 
                            class="form-control" 
                            id="commitMessageArea" 
                            rows="3" 
							placeholder={m.team_sync_commit_message_placeholder()}
							aria-describedby="commitMessageHelp"></textarea>
						<div id="commitMessageHelp" class="form-text">{m.team_sync_commit_message_help()}</div>
                    </div>					
					<button type="button" class="btn btn-light float-end" id="syncCancelBtn" aria-label={m.team_sync_cancel_label()} onclick={hideModal}>{m.team_sync_cancel_button()}</button>
					<button type="button" class="btn btn-primary float-end mx-2" id="syncChangesBtn" aria-label={m.team_sync_start_label()} onclick={syncProject}>{m.team_sync_sync_changes_button()}</button>
                </form>
                {/if}			
            </div>
        </div>
    </div>
</div>
